// AXA REALĂ a unei grinzi din modelul FEM, pentru desen.
//
// Modelul ține grinda între NODURILE a două elemente verticale (`nod_i`,
// `nod_j`). Capătul ei real — locul unde stă betonul grinzii — e nodul plus
// offsetul rigid de capăt (`ofs_a_xy` / `ofs_b_xy`, metri, în cadrul în care
// stau nodurile): la un stâlp în L sau T nodul e la intersecția axelor de
// pereți, iar grinda peretelui vecin stă pe axa lui, la 15–90 cm de nod; la o
// grindă de zid care pleacă din capătul unei lamele, bucata are metri. În
// calcul bucata nod → capăt e zonă rigidă; în desen ea e un BRAȚ, nu grindă.
// Trasată nod-la-nod, grinda ieșea oblică, tăind zidurile, iar un braț de 4 m
// se citea drept o grindă strâmbă.
//
// Un singur loc pentru toate scenele (Pasul 7, «Unde sunt?», armătura):
// aceleași capete, același prag de la care brațul se desenează.

import type { FemGrinda } from "./tipuri";

export type Pct = { x_m: number; y_m: number };
export type Xy = [number, number];

/** Sub atât brațul NU se desenează: e rotunjirea nodului pe axa zidului
 *  (1–4 cm), nu un braț pe care cineva să-l citească. Același prag ca pe
 *  server, unde planul 2D își stanțează brațele (`model.BRAT_RIGID_MIN_M`). */
export const BRAT_RIGID_MIN_M = 0.05;

/** Un braț rigid: de la nodul elementului la capătul real al grinzii. */
export interface Brat { nod: Xy; cap: Xy; L: number }

export interface AxaGrinzii {
  /** capetele REALE ale grinzii (nod + offset) */
  a: Xy;
  b: Xy;
  /** lungimea axei reale și unghiul ei (atan2, radiani) */
  L: number;
  unghi: number;
  /** brațele de desenat, în ordinea capetelor (a, apoi b), numai cele ≥ prag */
  brate: Brat[];
}

/** Lungimea offsetului de capăt (m); 0 fără offset. */
export function lungimeBrat(ofs?: [number, number] | null): number {
  return ofs ? Math.hypot(ofs[0], ofs[1]) : 0;
}

/** Grinda are cel puțin un braț de desenat? */
export function areBrate(g: Pick<FemGrinda, "ofs_a_xy" | "ofs_b_xy">): boolean {
  return lungimeBrat(g.ofs_a_xy) >= BRAT_RIGID_MIN_M
    || lungimeBrat(g.ofs_b_xy) >= BRAT_RIGID_MIN_M;
}

/** Axa reală a grinzii `g` între nodurile `nodA` (= `nod_i`) și `nodB`
 *  (= `nod_j`), cu brațele ei. Fără offseturi e chiar segmentul nod-la-nod. */
export function axaGrinzii(g: Pick<FemGrinda, "ofs_a_xy" | "ofs_b_xy">,
                           nodA: Pct, nodB: Pct): AxaGrinzii {
  const oa = g.ofs_a_xy ?? [0, 0];
  const ob = g.ofs_b_xy ?? [0, 0];
  const a: Xy = [nodA.x_m + oa[0], nodA.y_m + oa[1]];
  const b: Xy = [nodB.x_m + ob[0], nodB.y_m + ob[1]];
  const brate: Brat[] = [];
  for (const [nod, cap, ofs] of [
    [[nodA.x_m, nodA.y_m], a, oa], [[nodB.x_m, nodB.y_m], b, ob],
  ] as [Xy, Xy, [number, number]][]) {
    const L = lungimeBrat(ofs);
    if (L >= BRAT_RIGID_MIN_M) brate.push({ nod, cap, L });
  }
  return { a, b, L: Math.hypot(b[0] - a[0], b[1] - a[1]),
           unghi: Math.atan2(b[1] - a[1], b[0] - a[0]), brate };
}
